import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActiveInstallationContextProps } from '../types'
import { useActiveInstallation } from 'src/Hooks'
import { ActiveInstallationContext } from './active-installation-context'
import { useTypedSelector } from 'src/State/RootReducer'
import useDeviceLinks from '../hooks/use-device-links'
import { DeviceLinkModel } from '../models'
import { useStorageById } from 'src/Libs/installation-details/hooks/use-storage-by-id'
import { useUserRoles } from 'src/Hooks/user'
import { isFlowTheme } from 'src/Libs/utils'
import { StatusCodes } from 'src/Libs/http-status-codes'

interface Props {
  children?: React.ReactNode
}

/**
 * Provides context for the active installation
 */
const ActiveInstallationProvider: React.FC<Props> = ({ children }) => {
  const installationId = useTypedSelector(state => state.storage.activeInstallation)
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const { getDeviceLinks } = useDeviceLinks()
  const [deviceLinks, setDeviceLinks] = useState<DeviceLinkModel[]>([])
  const [storage, storageLoading, storageError, storageErrorStatus] = useStorageById(storageId ?? null)
  const [storageDeviceLink, setStorageDeviceLink] = useState<DeviceLinkModel>()
  const [deviceLinksLoading, setDeviceLinksLoading] = useState<boolean>(true)
  const latestDeviceLinksRequestRef = useRef(0)
  const { isServiceUser } = useUserRoles()
  const useDecommissionedAtCondition = !isServiceUser || isFlowTheme

  const {
    isLoading: installationLoading,
    hasError: installationError,
    installation,
    refreshInstallation,
    errorStatus: installationErrorStatus,
  } = useActiveInstallation(true, installationId?.toString() ?? '', storageId ?? '')

  const fetchDeviceLinks = useCallback(async (): Promise<void> => {
    const requestId = ++latestDeviceLinksRequestRef.current
    if (!installationId || installationErrorStatus === StatusCodes.FORBIDDEN || storageErrorStatus === StatusCodes.FORBIDDEN) {
      setStorageDeviceLink(undefined)
      setDeviceLinks([])
      setDeviceLinksLoading(false)
      return
    }
    setDeviceLinksLoading(true)
    try {
      const deviceLinks = await getDeviceLinks(installationId, useDecommissionedAtCondition)
      // AF-5937: Ignore responses superseded by a newer installation/storage request to avoid overwriting the current link with a stale value.
      if (requestId !== latestDeviceLinksRequestRef.current) return
      const storageDeviceLink = deviceLinks.value?.find(deviceLink => deviceLink.deviceId === storageId)
      setStorageDeviceLink(storageDeviceLink)
      setDeviceLinks(deviceLinks.value ?? [])
    } catch (error) {
      console.error('could not fetch devicelinks')
    } finally {
      if (requestId === latestDeviceLinksRequestRef.current) setDeviceLinksLoading(false)
    }
  }, [installationId, storageId, useDecommissionedAtCondition]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchDeviceLinks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationId, storageId])

  const value: ActiveInstallationContextProps = useMemo(() => {
    return {
      installationLoading,
      installationError,
      installationErrorStatus,
      installation,
      installationId,
      storageId,
      deviceLinks,
      storage: storage ?? undefined,
      storageLoading,
      storageError,
      storageErrorStatus,
      storageDeviceLink,
      deviceLinksLoading,
      refreshInstallation,
      refreshDeviceLinks: fetchDeviceLinks,
    }
  }, [
    /* eslint-disable-line */ installationId,
    installationError,
    installationErrorStatus,
    storageId,
    installationLoading,
    installation,
    deviceLinks,
    storage,
    storageLoading,
    storageError,
    storageErrorStatus,
    storageDeviceLink,
    deviceLinksLoading,
    refreshInstallation,
    fetchDeviceLinks,
  ])

  return <ActiveInstallationContext.Provider value={value}>{children}</ActiveInstallationContext.Provider>
}

export default ActiveInstallationProvider
